import React, { useState } from 'react';
import { useTheme } from '../../../../Context/ThemeContext';
import { PlayerAchievementItem } from '../PlayerHandler';

interface PlayerAchievementsProps {
  achievements: PlayerAchievementItem[];
}

const PAGE_SIZE = 5;

const PlayerAchievements: React.FC<PlayerAchievementsProps> = ({ achievements }) => {
  const { theme, isMobile } = useTheme();
  const [currentPage, setCurrentPage] = useState(1);

  const totalPages = Math.ceil(achievements.length / PAGE_SIZE);
  const startIdx = (currentPage - 1) * PAGE_SIZE;
  const visibleAchievements = achievements.slice(startIdx, startIdx + PAGE_SIZE);

  const styles: Record<string, React.CSSProperties> = {
    container: {
      background: theme.card,
      borderRadius: '10px',
      padding: isMobile ? '12px' : '16px',
      marginBottom: '16px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    },
    title: {
      fontSize: '16px',
      fontWeight: 700,
      color: theme.text,
      marginBottom: '12px',
    },
    grid: {
      display: 'flex',
      flexDirection: 'column' as const,
      gap: '6px',
    },
    badge: {
      padding: '8px 14px',
      borderRadius: '6px',
      background: 'rgba(34, 197, 94, 0.15)',
      border: '1px solid rgba(34, 197, 94, 0.3)',
      color: '#22c55e',
      fontWeight: 600,
      fontSize: '13px',
      textAlign: 'center' as const,
    },
    empty: {
      color: theme.textSecondary,
      fontSize: '14px',
      fontStyle: 'italic' as const,
    },
    pagination: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      gap: '8px',
      paddingTop: '12px',
    },
    pageBtn: {
      padding: '4px 10px',
      fontSize: '13px',
      fontWeight: 500,
      border: `1px solid ${theme.inputBorder}`,
      borderRadius: '6px',
      background: theme.inputBg,
      color: theme.text,
      cursor: 'pointer',
    },
    pageBtnDisabled: {
      opacity: 0.5,
      cursor: 'not-allowed',
    },
  };

  return (
    <div style={styles.container}>
      <div style={styles.title}>Achievements</div>
      {achievements.length === 0 ? (
        <div style={styles.empty}>No achievements yet</div>
      ) : (
        <>
          <div style={styles.grid}>
            {visibleAchievements.map((ach, i) => (
              <div key={startIdx + i} style={styles.badge} title={ach.description || ''}>
                {ach.title}
              </div>
            ))}
          </div>
          {totalPages > 1 && (
            <div style={styles.pagination}>
              <button
                style={{ ...styles.pageBtn, ...(currentPage === 1 ? styles.pageBtnDisabled : {}) }}
                onClick={() => setCurrentPage(p => p - 1)}
                disabled={currentPage === 1}
              >
                Previous
              </button>
              <span style={{ fontSize: '13px', color: theme.text }}>
                {currentPage} / {totalPages}
              </span>
              <button
                style={{ ...styles.pageBtn, ...(currentPage >= totalPages ? styles.pageBtnDisabled : {}) }}
                onClick={() => setCurrentPage(p => p + 1)}
                disabled={currentPage >= totalPages}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default PlayerAchievements;
